import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import Header from '@/components/Header';
import AdvertisingBanner from '@/components/AdvertisingBanner';
import LiveBadge from '@/components/LiveBadge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Play, Loader2, CheckCircle, TrendingUp, Heart, Share2, ShoppingBag, Search, X } from 'lucide-react';

const BoutiquePage = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentLive, setCurrentLive] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Nouveau: barre de recherche
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [priceRange, setPriceRange] = useState([1, 100]);
  
  // Paywall modal
  const [showPaywall, setShowPaywall] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  // Payment
  const [paymentMethod, setPaymentMethod] = useState('ORANGE_MONEY');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [purchaseId, setPurchaseId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // Likes state
  const [likedVideos, setLikedVideos] = useState(new Set());

  useEffect(() => {
    // Check if logged in
    const token = localStorage.getItem('dyvy_token');
    setIsLoggedIn(!!token);
    
    fetchVideos();
    fetchCurrentLive();
    
    // Poll current live every 10 seconds
    const interval = setInterval(fetchCurrentLive, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [videos, filterType, priceRange, searchQuery]); // Ajout de searchQuery

  useEffect(() => {
    if (purchaseId && paymentStatus === 'processing') {
      const interval = setInterval(checkPaymentStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [purchaseId, paymentStatus]);

  const fetchVideos = async () => {
    try {
      const res = await api.get('/boutique');
      setVideos(res.data);
      setFilteredVideos(res.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentLive = async () => {
    try {
      const res = await api.get('/live-streaming/current-public');
      setCurrentLive(res.data.current_live);
    } catch (error) {
      console.error('Error fetching current live:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...videos];
    
    // Filtre par recherche (prioritaire)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(video => {
        const title = (video.title || video.titre || '').toLowerCase();
        const artist = (video.artist_name || video.user_name || '').toLowerCase();
        const description = (video.description || '').toLowerCase();
        const category = (video.category || video.categorie || '').toLowerCase();
        
        return title.includes(query) || 
               artist.includes(query) || 
               description.includes(query) ||
               category.includes(query);
      });
    }
    
    // Filtre par type
    if (filterType === 'clips') {
      filtered = filtered.filter(v => v.file_type === 'mp4');
    } else if (filterType === 'audios') {
      filtered = filtered.filter(v => v.file_type === 'mp3');
    } else if (filterType === 'nouveautes') {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    filtered = filtered.filter(v => v.price_usd >= priceRange[0] && v.price_usd <= priceRange[1]);
    
    setFilteredVideos(filtered);
  };

  const checkPaymentStatus = async () => {
    try {
      const res = await api.get(`/boutique/${selectedVideo.id}/purchase/${purchaseId}/status`);
      setPaymentStatus(res.data.status);
      
      if (res.data.status === 'success') {
        toast.success('Paiement réussi ! Accédez à la vidéo');
      } else if (res.data.status === 'failed') {
        toast.error('Le paiement a échoué');
        setPaymentStatus(null);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleWatchClick = async (video) => {
    setSelectedVideo(video);
    
    // Must be logged in to purchase
    if (!isLoggedIn) {
      toast.error('Connectez-vous pour acheter');
      navigate('/auth');
      return;
    }
    
    // Check if already purchased
    try {
      const purchases = await api.get('/my-purchases');
      const alreadyPurchased = purchases.data.find(p => p.video_id === video.id);
      
      if (alreadyPurchased) {
        // Redirect to player
        navigate(`/player/${video.id}`);
        return;
      }
    } catch (error) {
      // Error checking purchases
    }
    
    // Show paywall
    setShowPaywall(true);
  };

  const handlePayment = async () => {
    if (!phoneNumber) {
      toast.error('Veuillez entrer votre numéro de téléphone');
      return;
    }

    setPaymentLoading(true);
    try {
      const res = await api.post(`/boutique/${selectedVideo.id}/purchase`, {
        payment_method: paymentMethod,
        phone_number: phoneNumber
      });
      
      setPurchaseId(res.data.id);
      setPaymentStatus(res.data.status);
      toast.success('Paiement initié ! Confirmez sur votre téléphone');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleAccessVideo = () => {
    setShowPaywall(false);
    navigate(`/player/${selectedVideo.id}`);
  };
  
  // Like/Unlike video
  const handleLikeVideo = async (videoId, e) => {
    e.stopPropagation();
    
    const isLiked = likedVideos.has(videoId);
    const endpoint = isLiked ? `/videos/${videoId}/unlike` : `/videos/${videoId}/like`;
    
    try {
      const res = await api.post(endpoint);
      
      // Update local likes count
      setVideos(videos.map(v => 
        v.id === videoId ? { ...v, likes: res.data.likes } : v
      ));
      
      // Update liked state
      const newLiked = new Set(likedVideos);
      if (isLiked) {
        newLiked.delete(videoId);
      } else {
        newLiked.add(videoId);
      }
      setLikedVideos(newLiked);
      
    } catch (error) {
      console.error('Error liking video:', error);
    }
  };
  
  // Share video
  const handleShareVideo = (video, e) => {
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/boutique?video=${video.id}`;
    const shareText = `🎬 ${video.title} par @${video.artist_name} - ${video.price_usd}$ sur AGRP BOX`;
    
    if (navigator.share) {
      navigator.share({
        title: video.title,
        text: shareText,
        url: shareUrl
      }).catch(err => console.log('Error sharing:', err));
    } else {
      // Copier le lien
      navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié dans le presse-papier !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
            🔥 <span className="text-[#E50914]">AGRP BOX</span> - Vendez vos clips vidéo sur AGRP
          </h1>
          <p className="text-lg sm:text-xl text-zinc-300 max-w-3xl mx-auto">
            Payez entre 1$ à 100$ pour voir un clip vidéo et regardez-le sur AGRP BOX pendant une année. Zéro téléchargement, streaming seulement.
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
              <Input
                type="text"
                placeholder="Rechercher une vidéo, un artiste, un titre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-6 text-lg bg-zinc-900/50 border-2 border-zinc-700 text-white placeholder-zinc-500 rounded-xl focus:border-[#E50914] focus:ring-[#E50914] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-zinc-400 text-sm mt-2 text-center">
                {filteredVideos.length} résultat{filteredVideos.length > 1 ? 's' : ''} trouvé{filteredVideos.length > 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>

        {/* Bannière Publicitaire - Visible pour tous */}
        <AdvertisingBanner />

        {/* SECTION: VIDÉOS DISPONIBLES (de la base de données) */}
        {filteredVideos && filteredVideos.length > 0 && (
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
              <ShoppingBag className="text-[#E50914]" />
              Vidéos Disponibles à l'Achat
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.map((video) => (
                <div 
                  key={video.id}
                  onClick={() => handleWatchClick(video)}
                  className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-[#E50914] transition-all group cursor-pointer"
                >
                  {/* Miniature */}
                  <div className="aspect-video relative bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                    {video.cover_image_path ? (
                      video.miniature_type === 'video' ? (
                        <video
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${video.cover_image_path}`}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${video.cover_image_path}`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )
                    ) : (
                      <div className="text-zinc-500 text-6xl">🎬</div>
                    )}
                    
                    {/* Play button overlay - Seulement si ce n'est PAS une vidéo teaser */}
                    {video.miniature_type !== 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/60 transition-all">
                        <div className="w-16 h-16 rounded-full bg-[#E50914] flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play size={28} className="text-white ml-1" fill="white" />
                        </div>
                      </div>
                    )}

                    {/* Badge prix */}
                    <div className="absolute top-4 right-4 bg-[#E50914] px-3 py-1 rounded-full">
                      <span className="text-white font-bold">{video.price_usd}$</span>
                    </div>
                  </div>

                  {/* Infos vidéo */}
                  <div className="p-4">
                    <h3 className="text-white font-bold text-lg mb-2 line-clamp-1">
                      {video.title}
                    </h3>
                    
                    {/* Nom de l'utilisateur - BIEN VISIBLE */}
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-zinc-500 text-sm">Publié par :</span>
                      <span className="text-white font-semibold text-sm bg-zinc-800 px-2 py-1 rounded">
                        {video.artist_name || 'Utilisateur'}
                      </span>
                    </div>
                    
                    <p className="text-zinc-400 text-sm mb-3 line-clamp-2">
                      {video.description || 'Aucune description'}
                    </p>
                    
                    {/* Date de sortie */}
                    {video.date_sortie && (
                      <div className="mb-3">
                        <span className="text-xs text-purple-400 font-semibold bg-purple-900/30 px-2 py-1 rounded">
                          📅 Sortie : {new Date(video.date_sortie).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-zinc-500 mb-3">
                      <span>👁️ {video.views || 0} vues</span>
                      <span>❤️ {video.likes || 0}</span>
                      <span>🛒 {video.purchases || 0} {video.purchases > 1 ? 'personnes' : 'personne'}</span>
                    </div>

                    {/* Boutons d'action */}
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-[#E50914] hover:bg-[#b8070f] text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWatchClick(video);
                        }}
                      >
                        <Lock size={16} className="mr-2" />
                        Acheter {video.price_usd}$
                      </Button>
                      
                      <Button 
                        variant="outline"
                        className="border-zinc-700 hover:border-[#E50914] text-white"
                        onClick={(e) => handleShareVideo(video, e)}
                      >
                        <Share2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SECTION: SERVICES AGRP BOX (3 cartes fixes) */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-6">
            Nos Services
          </h2>
        </div>

        {/* Videos Grid - 3 SERVICES AGRP BOX */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* CARTE 1 - VEVO DISTRIBUTION */}
          <div className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-[#E50914] transition-all group">
            <div className="aspect-video relative bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
              {/* Miniature vidéo */}
              <img 
                src="https://i.ytimg.com/vi/o8pl4orZDlE/maxresdefault.jpg" 
                alt="VEVO Distribution"
                className="w-full h-full object-cover"
              />
              {/* Logo VEVO en overlay */}
              <div className="absolute top-4 left-4 bg-red-600 px-4 py-2 rounded-md">
                <span className="text-white font-black text-2xl tracking-wider">VEVO</span>
              </div>
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/60 transition-all">
                <div className="w-16 h-16 rounded-full bg-[#E50914] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={28} className="text-white ml-1" fill="white" />
                </div>
              </div>
            </div>
            <div className="p-4">
              <h3 className="text-white font-bold text-xl mb-2">Clip Musical Premium</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Nous distribuons votre clip sur votre chaîne officielle VEVO
              </p>
              <div className="flex items-center justify-between">
                <span className="text-[#E50914] font-bold text-2xl">25$</span>
                <Button 
                  className="bg-[#E50914] hover:bg-[#F8212C] text-white px-6 py-2 font-semibold"
                  onClick={() => toast.info('Contactez-nous pour distribuer sur VEVO')}
                >
                  Acheter
                </Button>
              </div>
            </div>
          </div>

          {/* CARTE 2 - DISTRIBUTION MUSICALE (Spotify, Apple Music, etc.) */}
          <div className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-[#E50914] transition-all group">
            <div className="aspect-video relative bg-zinc-900">
              {/* Vidéo MP4 hébergée */}
              <video
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              >
                <source src="https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/55cc50b6840dc79e_Teaser%20J%27ai%20la%20victoire%20-%20Raoul%20Mavuna.mp4#t=0.1" type="video/mp4" />
              </video>
            </div>
            <div className="p-4">
              <h3 className="text-white font-bold text-xl mb-2">Distribution Musicale</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Vos chansons seront jouées sur Spotify, Apple Music et 150+ plateformes après distribution
              </p>
              <div className="flex items-center justify-between">
                <span className="text-[#E50914] font-bold text-2xl">25$</span>
                <Button 
                  className="bg-[#E50914] hover:bg-[#F8212C] text-white px-6 py-2 font-semibold"
                  onClick={() => navigate('/dashboard')}
                >
                  Distribuer
                </Button>
              </div>
            </div>
          </div>

          {/* CARTE 3 - AGRP BOX VENTE DIRECTE */}
          <div className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-[#E50914] transition-all group">
            <div className="aspect-video relative bg-zinc-900">
              {/* Vidéo MP4 hébergée */}
              <video
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              >
                <source src="https://customer-assets-agu9un31.emergentagent.net/job_dyvy-musicbox/artifacts/fbc2dd2c4e2de6dd_Raoul%20Mavuna%20-%20Break%20dance%20%28La%20Joie%20de%20l%E2%80%99%C3%89ternel%29.mp4#t=0.1" type="video/mp4" />
              </video>
            </div>
            <div className="p-4">
              <h3 className="text-white font-bold text-xl mb-2">Vendez sur AGRP BOX</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Vendez vos clips vidéos directement sur agrpbox et encaissez périodiquement sur Mobile Money
              </p>
              <div className="flex items-center justify-between">
                <span className="text-[#E50914] font-bold text-2xl">25$</span>
                <Button 
                  className="bg-[#E50914] hover:bg-[#F8212C] text-white px-6 py-2 font-semibold"
                  onClick={() => toast.success('Contactez-nous pour vendre vos clips sur AGRP BOX')}
                >
                  Vendre
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center glass rounded-xl p-8">
          <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit' }}>
            Deviens Artiste Vendeur
          </h3>
          <p className="text-zinc-400 mb-6">
            Vends tes clips et audios. Fixe ton prix. Garde 70% des revenus.
          </p>
          <Button
            onClick={() => navigate('/mon-espace-artiste')}
            className="bg-[#E50914] hover:bg-[#F8212C] text-white font-semibold px-8 py-6 text-lg"
          >
            VENDRE VOS VIDÉOS 18$
          </Button>
        </div>
      </main>

      {/* Paywall Modal */}
      <Dialog open={showPaywall} onOpenChange={setShowPaywall}>
        <DialogContent className="glass border-zinc-800 max-w-md">
          {paymentStatus === 'success' ? (
            <div className="text-center py-8">
              <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Paiement réussi !</h3>
              <p className="text-zinc-400 mb-6">Vous pouvez maintenant accéder à la vidéo</p>
              <Button
                onClick={handleAccessVideo}
                className="bg-green-600 hover:bg-green-700 w-full"
              >
                <Play className="mr-2" size={18} />
                ACCÉDER À LA VIDÉO
              </Button>
            </div>
          ) : paymentStatus === 'processing' ? (
            <div className="text-center py-8">
              <Loader2 size={64} className="text-[#E50914] animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Paiement en cours...</h3>
              <p className="text-zinc-400">Veuillez confirmer sur votre téléphone</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center justify-center mb-4">
                  <div className="bg-zinc-900 rounded-full p-4">
                    <Lock size={32} className="text-[#E50914]" />
                  </div>
                </div>
                <DialogTitle className="text-center text-2xl">⚠️ Contenu Exclusif AGRP BOX</DialogTitle>
                <DialogDescription className="text-center text-zinc-400 text-base">
                  Payez <span className="text-[#F97316] font-bold">{selectedVideo?.price_usd}$</span> une fois pour regarder. 
                  <br />
                  <span className="text-xs text-zinc-500 mt-2 block">Pas d'aperçu. Streaming seulement après paiement.</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Video Info */}
                <div className="bg-zinc-900 rounded-lg p-4">
                  <h4 className="font-semibold mb-1">{selectedVideo?.title}</h4>
                  <p className="text-sm text-zinc-400">Par @{selectedVideo?.artist_name}</p>
                </div>

                <div>
                  <Label>Méthode de paiement</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                      <SelectItem value="AIRTEL_MONEY">Airtel Money</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Numéro de téléphone</Label>
                  <Input
                    type="tel"
                    placeholder="+243..."
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>

                <div className="bg-zinc-900/50 rounded-lg p-3 text-xs text-zinc-400 space-y-1">
                  <p>✓ Accès après paiement</p>
                  <p>✓ Streaming illimité</p>
                  <p>✓ Zéro téléchargement (anti-piratage)</p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowPaywall(false)}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handlePayment}
                    disabled={paymentLoading}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {paymentLoading ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      `PAYER ${selectedVideo?.price_usd}$`
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoutiquePage;